import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatDate } from '@/lib/utils';
import { getProbabilityColor } from '@/constants/timeline';
import { SourceIdBadge } from '@/components/atoms';
import type { DateFrequency } from '@/types/api';

export interface DistributionChartProps {
  data: DateFrequency[];
  color: string;
  dueDate?: string | null;
  /** Probability of meeting the due date (0-1). Used for color-coding the due date line. */
  dueDateProbability?: number | null;
  title: string;
  /** External issue ID (e.g. "PROJ-123"); shown as a badge beside the title */
  sourceId?: string | null;
  showCumulative?: boolean;
}

interface ChartDataPoint {
  date: string;
  timestamp: number;
  displayDate: string;
  frequency: number;
  cumulative: number;
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) {
  if (active && payload && payload.length > 0 && payload[0]) {
    const point = payload[0].payload;
    return (
      <div className="rounded-lg border border-border-default bg-bg-elevated p-3 shadow-lg">
        <p className="text-sm font-medium text-text-primary">{point.displayDate}</p>
        <p className="mt-1 text-xs text-text-muted">
          Frequency: <span className="text-text-primary">{point.frequency}</span>
        </p>
        <p className="text-xs text-text-muted">
          Cumulative: <span className="text-text-primary">{point.cumulative}%</span>
        </p>
      </div>
    );
  }
  return null;
}

export function DistributionChart({
  data,
  color,
  dueDate,
  dueDateProbability,
  title,
  sourceId,
  showCumulative = true,
}: DistributionChartProps) {
  // Get color based on probability of meeting due date
  const dueDateColor = getProbabilityColor(dueDateProbability ?? null);
  // Convert data with timestamps for time-based x-axis
  const { chartData, dueDateTimestamp, xDomain } = useMemo(() => {
    const points: ChartDataPoint[] = data.map((d) => ({
      date: d.date,
      timestamp: new Date(d.date).getTime(),
      displayDate: formatDate(d.date),
      frequency: d.frequency,
      cumulative: Math.round(d.cumulative_probability * 100),
    }));

    // Calculate due date timestamp if provided
    const dueTs = dueDate ? new Date(dueDate).getTime() : null;

    // Calculate domain that includes due date if needed
    if (points.length === 0) {
      return { chartData: points, dueDateTimestamp: dueTs, xDomain: ['auto', 'auto'] as [string, string] };
    }

    const timestamps = points.map((p) => p.timestamp);
    let minTs = Math.min(...timestamps);
    let maxTs = Math.max(...timestamps);

    // Extend domain to include due date if it's set
    if (dueTs !== null) {
      minTs = Math.min(minTs, dueTs);
      maxTs = Math.max(maxTs, dueTs);
    }

    return { chartData: points, dueDateTimestamp: dueTs, xDomain: [minTs, maxTs] as [number, number] };
  }, [data, dueDate]);

  if (data.length === 0) {
    return <div className="flex h-50 items-center justify-center text-text-muted">No data available</div>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium text-text-secondary">{title}</h4>
        {sourceId && <SourceIdBadge sourceId={sourceId} variant="compact" size="sm" />}
      </div>
      <div className="h-50">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`gradient-${title}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border-default" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={xDomain}
              tickFormatter={(ts: number) => {
                const isoDate = new Date(ts).toISOString().split('T')[0] ?? '';
                return formatDate(isoDate);
              }}
              tick={{ className: 'fill-text-muted', fontSize: 10 }}
              tickLine={{ className: 'stroke-border-subtle' }}
              axisLine={{ className: 'stroke-border-subtle' }}
              minTickGap={50}
            />
            <YAxis
              yAxisId="left"
              tick={{ className: 'fill-text-muted', fontSize: 10 }}
              tickLine={{ className: 'stroke-border-subtle' }}
              axisLine={{ className: 'stroke-border-subtle' }}
              label={{
                value: 'Frequency',
                angle: -90,
                position: 'insideLeft',
                className: 'fill-text-muted',
                fontSize: 10,
              }}
            />
            {showCumulative && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ className: 'fill-text-muted', fontSize: 10 }}
                tickLine={{ className: 'stroke-border-subtle' }}
                axisLine={{ className: 'stroke-border-subtle' }}
                tickFormatter={(value: number) => `${value}%`}
                label={{
                  value: 'Cumulative %',
                  angle: 90,
                  position: 'insideRight',
                  className: 'fill-text-muted',
                  fontSize: 10,
                }}
              />
            )}
            <Tooltip content={<CustomTooltip />} />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="frequency"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${title})`}
            />
            {showCumulative && (
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="cumulative"
                stroke="var(--color-feedback-info)"
                strokeWidth={1}
                strokeDasharray="4 4"
                fill="none"
              />
            )}
            {dueDateTimestamp && (
              <ReferenceLine
                x={dueDateTimestamp}
                yAxisId="left"
                stroke={dueDateColor}
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{
                  value:
                    dueDateProbability !== null && dueDateProbability !== undefined
                      ? `${Math.round(dueDateProbability * 100)}%`
                      : 'Due',
                  position: 'top',
                  fill: dueDateColor,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
